import { Icon } from '@iconify/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useCampaignDashboard } from '@/hooks/useCampaignDashboard'
import { useCampaignDonors } from '@/hooks/useCampaignDonors'
import { useCampaignSales } from '@/hooks/useCampaignSales'
import { canViewCampaignDashboard } from '@/lib/collaborator-permissions'
import {
  deriveDashboardInsights,
  getCampaignDisplayName,
  getDefaultDashboardRange,
  getAllPeriodRange,
  getPresetRange,
  isSameRange,
  type DashboardDateRange,
} from '@/lib/campaign-dashboard'
import { CampaignDashboardChart } from './CampaignDashboardChart'
import { CampaignDashboardFilters } from './CampaignDashboardFilters'
import { CampaignDashboardHeader } from './CampaignDashboardHeader'
import { CampaignDashboardHealth } from './CampaignDashboardHealth'
import { CampaignDashboardKpis } from './CampaignDashboardKpis'
import { CampaignDashboardRecentActivity } from './CampaignDashboardRecentActivity'
import { CampaignDonorsTable } from './CampaignDonorsTable'
import { CampaignInfoPanel } from './CampaignInfoPanel'
import { CampaignSalesTable } from './CampaignSalesTable'
import { CampaignUrlAttributionPanel } from './CampaignUrlAttributionPanel'
import styles from './CampaignDashboard.module.css'

interface CampaignDashboardContainerProps {
  campaignId: number | null
  syncFiltersWithUrl?: boolean
}

function parseRangeFromParams(searchParams: URLSearchParams): DashboardDateRange {
  const dateFrom = searchParams.get('date_from')
  const dateTo = searchParams.get('date_to')
  const preset = searchParams.get('range')

  if (dateFrom && dateTo) {
    return {
      preset:
        preset === '7d' ||
        preset === '30d' ||
        preset === 'month' ||
        preset === 'all' ||
        preset === 'custom'
          ? preset
          : 'custom',
      dateFrom,
      dateTo,
    }
  }

  return getDefaultDashboardRange()
}

function renderKpiSkeleton() {
  return (
    <section className={styles.loadingCard}>
      <div className={styles.skeletonGrid}>
        <div className={styles.skeletonCard} />
        <div className={styles.skeletonCard} />
        <div className={styles.skeletonCard} />
        <div className={styles.skeletonCard} />
      </div>
    </section>
  )
}

export function CampaignDashboardContainer({
  campaignId,
  syncFiltersWithUrl = false,
}: CampaignDashboardContainerProps) {
  const { isCollaborator, hasPermission } = useAuth()
  const canViewDashboard = canViewCampaignDashboard(Boolean(isCollaborator), hasPermission)
  const [searchParams, setSearchParams] = useSearchParams()
  const [localFilters, setLocalFilters] = useState<DashboardDateRange>(() =>
    getDefaultDashboardRange()
  )
  const filters = syncFiltersWithUrl ? parseRangeFromParams(searchParams) : localFilters
  const [draftDateFrom, setDraftDateFrom] = useState(() => filters.dateFrom)
  const [draftDateTo, setDraftDateTo] = useState(() => filters.dateTo)
  const [donorsPage, setDonorsPage] = useState(1)
  const [salesPage, setSalesPage] = useState(1)

  const applyFilters = useCallback((next: DashboardDateRange) => {
    if (syncFiltersWithUrl) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('range', next.preset)
      nextParams.set('date_from', next.dateFrom)
      nextParams.set('date_to', next.dateTo)
      setSearchParams(nextParams, { replace: true })
    } else {
      setLocalFilters(next)
    }
  }, [syncFiltersWithUrl, searchParams, setSearchParams])

  useEffect(() => {
    const resetPages = window.setTimeout(() => {
      setDonorsPage(1)
      setSalesPage(1)
    }, 0)

    return () => window.clearTimeout(resetPages)
  }, [campaignId])

  const dashboard = useCampaignDashboard({
    campaignId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    enabled: canViewDashboard,
  })
  const donors = useCampaignDonors({
    campaignId,
    page: donorsPage,
    perPage: 10,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    enabled: canViewDashboard,
  })
  const sales = useCampaignSales({
    campaignId,
    page: salesPage,
    perPage: 10,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    enabled: canViewDashboard,
  })

  const insights = useMemo(
    () => (dashboard.data ? deriveDashboardInsights(dashboard.data) : null),
    [dashboard.data]
  )

  const handlePresetChange = (preset: '7d' | '30d' | 'month' | 'all') => {
    const next = getPresetRange(preset, new Date(), dashboard.data?.campaign.starts_at)
    applyFilters(next)
    setDraftDateFrom(next.dateFrom)
    setDraftDateTo(next.dateTo)
  }

  useEffect(() => {
    if (filters.preset !== 'all' || !dashboard.data?.campaign.starts_at) return

    const next = getAllPeriodRange(dashboard.data.campaign.starts_at)
    if (isSameRange(filters, next)) return

    const sync = window.setTimeout(() => {
      applyFilters(next)
      setDraftDateFrom(next.dateFrom)
      setDraftDateTo(next.dateTo)
    }, 0)

    return () => window.clearTimeout(sync)
  }, [dashboard.data?.campaign.starts_at, filters, syncFiltersWithUrl, applyFilters])

  const handleApplyCustomRange = () => {
    if (!draftDateFrom || !draftDateTo || draftDateFrom > draftDateTo) return
    applyFilters({ preset: 'custom', dateFrom: draftDateFrom, dateTo: draftDateTo })
  }

  const handleCopyPublicUrl = async () => {
    const slug = dashboard.data?.campaign.slug
    if (!slug) return
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/campanha/${slug}`)
    } catch {
      // noop
    }
  }

  if (!canViewDashboard) {
    return (
      <div className={styles.dashboardPage}>
        <section className={styles.emptyState}>
          <span className={styles.emptyIcon}>
            <Icon icon="mdi:lock-outline" />
          </span>
          <h2 className={styles.stateTitle}>Dashboard indisponível</h2>
          <p className={styles.stateText}>
            Você não tem permissão para visualizar o dashboard e as métricas desta campanha,
            incluindo Páginas de origem. Peça ao dono da instituição para habilitar a permissão
            Visualizar dashboard e métricas.
          </p>
        </section>
      </div>
    )
  }

  if (!campaignId) {
    return (
      <div className={styles.dashboardPage}>
        <section className={styles.emptyState}>
          <span className={styles.emptyIcon}>
            <Icon icon="mdi:bullhorn-outline" />
          </span>
          <h2 className={styles.stateTitle}>Nenhuma campanha selecionada</h2>
          <p className={styles.stateText}>
            Abra uma campanha especifica ou use a lista de campanhas para entrar no
            dashboard dedicado de uma campanha.
          </p>
        </section>
      </div>
    )
  }

  const periodFilters = (
    <CampaignDashboardFilters
      value={filters}
      draftDateFrom={draftDateFrom}
      draftDateTo={draftDateTo}
      onPresetChange={handlePresetChange}
      onDraftDateChange={(field, value) => {
        if (field === 'dateFrom') setDraftDateFrom(value)
        if (field === 'dateTo') setDraftDateTo(value)
      }}
      onApplyCustomRange={handleApplyCustomRange}
    />
  )

  const hasDashboardData = Boolean(dashboard.data && insights)
  const dashboardError =
    dashboard.error ||
    (!dashboard.isLoading && !hasDashboardData
      ? 'A campanha não retornou dados suficientes para montar o dashboard.'
      : null)

  return (
    <div className={styles.dashboardPage}>
      {hasDashboardData && dashboard.data && insights && (
        <CampaignDashboardHeader
          title={getCampaignDisplayName(dashboard.data.campaign)}
          campaign={dashboard.data.campaign}
          summary={dashboard.data.summary}
          onCopyPublicUrl={handleCopyPublicUrl}
        />
      )}

      <CampaignDashboardChart
        chart={dashboard.data?.sales_chart ?? null}
        insights={insights}
        filters={periodFilters}
        isLoading={dashboard.isLoading && !dashboard.data}
        error={hasDashboardData ? null : dashboardError}
        onRetry={() => void dashboard.refetch()}
      />

      {dashboard.isLoading && !dashboard.data ? (
        renderKpiSkeleton()
      ) : hasDashboardData && dashboard.data && insights ? (
        <>
          <CampaignDashboardKpis
            summary={dashboard.data.summary}
            previousSummary={dashboard.previousData?.summary ?? null}
          />
          <CampaignUrlAttributionPanel
            attribution={dashboard.data.url_attribution ?? { rows: [], total_origins: 0 }}
            isLoading={dashboard.isLoading && !dashboard.data}
            error={null}
            onRetry={() => void dashboard.refetch()}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            draftDateFrom={draftDateFrom}
            draftDateTo={draftDateTo}
            onDraftDateChange={(field, value) => {
              if (field === 'dateFrom') setDraftDateFrom(value)
              if (field === 'dateTo') setDraftDateTo(value)
            }}
            onApplyPeriod={handleApplyCustomRange}
          />
          <CampaignDashboardHealth
            campaign={dashboard.data.campaign}
            summary={dashboard.data.summary}
            insights={insights}
          />
          <CampaignDashboardRecentActivity
            recentDonors={dashboard.data.recent_donors}
            recentSales={dashboard.data.recent_sales}
          />
        </>
      ) : null}

      <CampaignDonorsTable
        data={donors.data}
        isLoading={donors.isLoading}
        error={donors.error}
        onRetry={() => void donors.refetch()}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        page={donorsPage}
        onPageChange={setDonorsPage}
      />

      <CampaignSalesTable
        data={sales.data}
        isLoading={sales.isLoading}
        error={sales.error}
        onRetry={() => void sales.refetch()}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        page={salesPage}
        onPageChange={setSalesPage}
      />

      {dashboard.data && <CampaignInfoPanel campaign={dashboard.data.campaign} />}
    </div>
  )
}
